import { useState } from "react";
import { KeyedMutator } from "swr";

import { Box, Button, Flex, FormDialog, Typography } from "flux";

import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import fetcher from "@adora/shared/utils/fetch";
import post from "@adora/shared/utils/post";
import { RelativeUrl } from "@adora/shared/utils/environment";
import { PresignedPost, PresignedPostDestinationPath } from "@adora/shared/types/presigned-post";
import { AdAsset } from "../../types/ad-asset";

interface BulkReplaceDialogProps extends BaseDialogProps {
  brandId: string;
  adAssetGroupId: string;
  refetchAdAssets: KeyedMutator<AdAsset[]>;
}

interface BulkReplaceRequest {
  [key: string]: number | string;
  ad_asset_group_id: number;
  folder_path: string;
}

function BulkReplaceDialog(props: BulkReplaceDialogProps) {
  const { onClose, brandId, adAssetGroupId, refetchAdAssets } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFiles([...event.target.files]);
    }
  };

  const getPresignedPost = async () => {
    return fetcher<PresignedPostDestinationPath>(`${RelativeUrl}/ad_asset_images/upload_url`, {
      brand_id: brandId
    });
  };

  const uploadFileToS3 = async (file: File, presignedPost: PresignedPost) => {
    const formData = new FormData();
    Object.keys(presignedPost.fields).forEach((key) => {
      formData.append(key, presignedPost.fields[key]);
    });

    formData.append("file", file, file.name);

    const uploadRequestOptions = {
      method: "POST",
      body: formData
    };

    const response = await fetch(presignedPost.url, uploadRequestOptions);
    if (response.status > 204) {
      throw new Error(`Failed to upload ${file.name}. Server returned status ${response.status}`);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!files.length) return;

    setUploading(true);
    try {
      // Get presigned post
      const { presigned_post: presignedPost, destination_path } = await getPresignedPost();

      // Upload all files to S3
      await Promise.all(files.map((file) => uploadFileToS3(file, presignedPost)));

      // Call bulk replace API
      const bulkReplaceRequest: BulkReplaceRequest = {
        ad_asset_group_id: Number(adAssetGroupId),
        folder_path: destination_path
      };

      const result = await post(
        `${RelativeUrl}/ad_asset_images/bulk/replace`,
        { brand_id: brandId },
        bulkReplaceRequest
      );

      result.match(
        (error) => {
          console.error("Error in bulk replace:", error);
          throw new Error("Failed to bulk replace images");
        },
        () => {
          // Success
          refetchAdAssets();
          onClose(true);
        }
      );
    } catch (error) {
      console.error("Error in bulk replace process:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <FormDialog
      {...props}
      onSubmit={handleSubmit}
      primaryButtonText={uploading ? "Uploading..." : "Submit"}
      title="Bulk Replace Ad Asset Images"
      isPrimaryButtonDisabled={uploading || files.length === 0}
      dataDogIdentifier="bulk replace ad asset images"
    >
      <Flex sx={{ width: "100%" }} pb="24px" flexDirection="column" gap="16px">
        <Box>
          <Typography variant="body2" mb={2}>
            Select multiple image files to replace ad asset images in this group.
          </Typography>
          <Typography variant="body2" mb={2}>
            The naming convention should be (ad_builder_image_id)_(width)x(height).png
          </Typography>
          <input
            type="file"
            onChange={handleFileChange}
            style={{ display: "none" }}
            id="bulk-file-upload-input"
            multiple
            accept="image/*"
          />
          <label htmlFor="bulk-file-upload-input">
            <Button variant="contained" component="span" dataDogIdentifier="bulk replace upload files">
              Select Files
            </Button>
          </label>
        </Box>

        {files.length > 0 && (
          <Box>
            <Typography variant="body2" mb={1}>
              Selected files ({files.length}):
            </Typography>
            <Box maxHeight="200px" overflow="auto">
              {files.map((file, index) => (
                <Typography key={index} variant="body2" color="textSecondary">
                  • {file.name}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Flex>
    </FormDialog>
  );
}

export default BulkReplaceDialog;
